// ============================================================
// A CHARGE (2026-10-21, the user's rulings; rules-spec 6A.3). A Charge is a
// run straight at the target and a strike at its end: from at least 4 m
// away (centre to centre, as the map's readout measures - 2026-10-28), no
// further than the charge distance (3 x Agility Bonus, as the conditions
// leave it), on a line a walker can run, stopping about a metre
// short - never on top of the target, as it used to. It used to demand the
// target already in reach, "which completely misses the point".
//
// The line is sampled every quarter metre: off the arena or on impassable
// ground it is blocked, difficult ground costs double, and a rise above one
// stride refuses it - a charge does not climb. A flyer (or anyone the
// terrain does not touch) takes the straight line and stops at the target's
// level. The ground is handed in, so this stays pure.
//
// Pure. Zero React dependency. Metres.
// ============================================================
import { reachGap } from './reach';
import type { Token } from './tokens';
import type { TerrainKind } from './types';
import type { Movement } from '../character/types';

/** How far away the target must be, centre to centre on the ground, in metres. */
const CHARGE_MIN_M = 4;

/** How far short of the target a charge stops, footprint to footprint, when the weapon reaches that far. */
const STOP_GAP_M = 1;

/** A hair inside the reach, so the stop is in reach however the arithmetic rounds. */
const SLACK_M = 0.05;

/** How finely the line is walked. */
const STEP_M = 0.25;

export type ChargeRefusal = 'cannotMove' | 'crawling' | 'tooClose' | 'tooFar' | 'blocked' | 'climb';

/** Why a Charge plate is dimmed, short. */
export const CHARGE_REASONS: Record<ChargeRefusal, string> = {
  cannotMove: 'You cannot move',
  crawling: 'No charge while prone, pinned or helpless',
  tooClose: 'Too close to charge: at least 4 m away needed',
  tooFar: 'Too far to charge',
  blocked: 'The way to the target is blocked',
  climb: 'A charge cannot climb',
};

/** The ground a charge runs over, as the map knows it here. */
export interface ChargeGround {
  /** The worst terrain at a ground point, for a walker at that height. */
  terrainAt(x: number, z: number, atHeight: number): TerrainKind;
  /** The surface a walker coming from `fromY` stands on at a ground point. */
  surfaceAt(x: number, z: number, fromY: number): number;
  /** Inside the arena. */
  inBounds(x: number, z: number): boolean;
  /** The most a walker rises in one stride, in metres. */
  maxStep: number;
  /** No terrain counts for this mover (a flyer, a phase walker, the incorporeal, terrain switched off). */
  free: boolean;
}

export type ChargePlan =
  | { ok: true; stop: { x: number; z: number; elevation: number }; metres: number; cost: number }
  | { ok: false; reason: ChargeRefusal; metres: number | null };

/** The postures a charge cannot start from: lying down or bound. */
const NO_CHARGE = new Set(['P', 'Pn', 'Hpl']);

/** How far this one charges, as the conditions have left the movement (`movementUnder`); or why they cannot. */
export function chargeBand(
  movement: Movement | undefined,
  conditions: readonly string[],
): { metres: number; refusal: ChargeRefusal | null } {
  if (conditions.some((c) => NO_CHARGE.has(c.split(':')[0]))) return { metres: 0, refusal: 'crawling' };
  const metres = movement?.charge ?? 0;
  return { metres, refusal: metres > 0 ? null : 'cannotMove' };
}

/** Where the run stops: on the line to the target, `gap` short of its body. */
function stopPoint(mover: Token, target: Token, gap: number, radius: number) {
  const apart = Math.hypot(mover.x - target.x, mover.z - target.z);
  const stopAt = mover.footprint + radius + gap;
  const k = apart > 0 ? (apart - stopAt) / apart : 0;
  return { x: mover.x + (target.x - mover.x) * k, z: mover.z + (target.z - mover.z) * k, run: apart - stopAt, apart };
}

/** One stride's ground: the height it lands on and what it costs per metre, or why it cannot be run. */
function stride(ground: ChargeGround, x: number, z: number, y: number): { y: number; per: number } | ChargeRefusal {
  const kind = ground.inBounds(x, z) ? ground.terrainAt(x, z, y) : 'impassable';
  if (kind === 'impassable') return 'blocked';
  const next = ground.surfaceAt(x, z, y);
  if (next - y > ground.maxStep) return 'climb';
  return { y: next, per: kind === 'difficult' ? 2 : 1 };
}

/** Walk the line: its cost (difficult ground double) and where it ends, or why it cannot be run. */
function walkLine(
  from: Token,
  to: { x: number; z: number },
  run: number,
  ground: ChargeGround,
): { cost: number; elevation: number } | { refusal: ChargeRefusal } {
  const steps = Math.max(1, Math.ceil(run / STEP_M));
  let y = from.elevation;
  let cost = 0;
  for (let i = 1; i <= steps; i++) {
    const step = stride(ground, from.x + ((to.x - from.x) * i) / steps, from.z + ((to.z - from.z) * i) / steps, y);
    if (typeof step === 'string') return { refusal: step };
    y = step.y;
    cost += (run / steps) * step.per;
  }
  return { cost, elevation: y };
}

/**
 * Plan a charge from `mover` at `target`: where it stops and how far it runs, or why it cannot be made.
 * `band` is the charge distance in metres (`chargeBand`); `reach` the weapon's. The stop is measured as the strike
 * measures it (`reachGap`, the target's token), so a run that lands can always strike.
 */
export function planCharge(
  mover: Token,
  target: Token,
  o: { band: number; reach: number; ground: ChargeGround },
): ChargePlan {
  const gap = Math.max(0, Math.min(STOP_GAP_M, o.reach) - SLACK_M);
  const { x, z, run, apart } = stopPoint(mover, target, gap, target.footprint);
  // The MINIMUM is read where the table reads distance: centre to centre (2026-10-28). It used to be the run left
  // after both bodies and the stop gap, so a target the readout put at 5 m was "too close". A body big enough to
  // leave no run at all from 4 m is already at the stop, and is too close as well.
  if (apart < CHARGE_MIN_M) return { ok: false, reason: 'tooClose', metres: apart };
  // Bodies so big there is nothing left to run: too close, but not by the 4 m - so no distance to quote (the review).
  if (run <= 0) return { ok: false, reason: 'tooClose', metres: null };
  const walked = o.ground.free ? { cost: run, elevation: target.elevation } : walkLine(mover, { x, z }, run, o.ground);
  if ('refusal' in walked) return { ok: false, reason: walked.refusal, metres: run };
  if (walked.cost > o.band + 1e-6) return { ok: false, reason: 'tooFar', metres: walked.cost };
  const stop = { x, z, elevation: walked.elevation };
  // A target on a ledge the run cannot reach: the stop is beside it, out of reach above or below.
  if (reachGap({ ...mover, ...stop }, target) > o.reach + 1e-6) {
    return { ok: false, reason: 'climb', metres: run };
  }
  return { ok: true, stop, metres: run, cost: walked.cost };
}

/**
 * A charge strikes once (2026-10-21): it is a Full Action of its own, so a Swift or a Lightning attack - each a Full
 * Action too - cannot ride it. Any other mode is the charge's.
 */
export function chargeFireMode(fireMode: string, charging: boolean): string {
  return charging && (fireMode === 'swift' || fireMode === 'lightning') ? 'single' : fireMode;
}

/** The log's sentence for a refused charge, in `reachReason`'s style; null when it was not refused. */
export function chargeLine(plan: ChargePlan, band: number): string | null {
  if (plan.ok) return null;
  if (plan.reason === 'tooFar' && plan.metres !== null) {
    return `Too far to charge: the run is ${plan.metres.toFixed(1)} m, and you charge ${band} m.`;
  }
  if (plan.reason === 'tooClose' && plan.metres !== null) {
    const away = plan.metres.toFixed(1);
    return `Too close to charge: the target is ${away} m away, and a charge needs ${CHARGE_MIN_M} m.`;
  }
  return `${CHARGE_REASONS[plan.reason]}.`;
}
